/**
 * Turning an API error body into a sentence a person can act on.
 *
 * Its own dependency-free module so the shapes can be tested — the failure here
 * is that a user sees JSON, which nobody notices until it happens in front of
 * them.
 *
 * Three shapes arrive, and only the first two were handled:
 *
 *   "Briefing not found"                     — a plain string, already a sentence
 *   {message, seat: [...]}                   — machine-readable fix + human line
 *   [{type, loc, msg, input, ctx}, ...]      — FastAPI 422 validation errors
 *
 * The third fell through to JSON.stringify, so a request that violated a query
 * constraint showed the reader
 * `[{"type":"less_than_equal","loc":["query","limit"],"msg":"Input should be
 * less than or equal to 100","input":"1000","ctx":{"le":100}}]`. Every 422 in
 * the app rendered like that.
 */

interface ValidationError {
  loc?: unknown[];
  msg?: string;
}

function isValidationErrorList(value: unknown): value is ValidationError[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => v && typeof v === 'object' && typeof (v as ValidationError).msg === 'string')
  );
}

/**
 * "limit: Input should be less than or equal to 100"
 *
 * `loc` is a path like ["query", "limit"] or ["body", "page_ids", 0]. The last
 * NAMED segment is the field the reader recognizes — the leading "query"/"body"
 * is plumbing, and a trailing array index is noise without the name.
 */
function describeValidationError(err: ValidationError): string {
  const named = (err.loc ?? []).filter((p): p is string => typeof p === 'string');
  const field = named.length > 1 ? named[named.length - 1] : named[0];
  const msg = err.msg ?? 'is not valid';
  return field && field !== 'body' && field !== 'query' ? `${field}: ${msg}` : msg;
}

export function apiErrorMessage(detail: unknown): string {
  if (typeof detail === 'string' && detail) return detail;

  if (isValidationErrorList(detail)) {
    // More than a couple and the list stops being readable; the count still
    // tells them it is not one stray field.
    const shown = detail.slice(0, 3).map(describeValidationError);
    const rest = detail.length - shown.length;
    return shown.join('; ') + (rest > 0 ? ` (and ${rest} more)` : '');
  }

  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }

  // Last resort. Better than an empty alert, and rare enough to be worth
  // seeing verbatim when it happens — but an empty array is truthy and would
  // otherwise reach the reader as the string "[]".
  if (Array.isArray(detail) && detail.length === 0) return 'Request failed';
  if (detail) return JSON.stringify(detail);
  return 'Request failed';
}
